import * as React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router-dom";
import { adminFetch } from "../../services/adminFetch";
import AdminImagesPanel from "./ImagesPanel";

type ExtraRow = { id?: number; title: string; value: string };
type FeatureRow = { id: number; name: string };

function calcCompleteness(p: any) {
  const missing = [
    !p.latitude,
    !p.longitude,
    !p.featured_image_url,
    !p.content,
    p.persons_max == null,
    p.price_per_night == null,
  ].filter(Boolean).length;
  const total = 6;
  const pct = Math.round(((total - missing) / total) * 100);
  return { pct, missing };
}

function FormGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
      }}
    >
      {children}
    </Box>
  );
}

function Col({
  xs = 12,
  md,
  children,
}: {
  xs?: number;
  md?: number;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        gridColumn: {
          xs: "1 / -1",
          md: `span ${md ?? xs}`,
        },
      }}
    >
      {children}
    </Box>
  );
}

function slugify(input: string) {
  return (input || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 190);
}

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
];

const LISTING_OPTIONS = [
  { value: "rent", label: "Rent" },
  { value: "sale", label: "Sale" },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: "apartment", label: "Apartment" },
  { value: "house", label: "House" },
  { value: "stonehouse", label: "Stone House" },
  { value: "villa", label: "Villa" },
  { value: "studio", label: "Studio" },
  { value: "maisonette", label: "Maisonette" },
  { value: "land", label: "Land" },
  { value: "plot", label: "Plot" },
  { value: "hotel", label: "Hotel" },
  { value: "other", label: "Other" },
];

export default function AdminEditPropertyPage() {
  const { locale, id } = useParams<{ locale?: string; id: string }>();
  const nav = useNavigate();
  const currentLocale = locale || "el";

  const [tab, setTab] = React.useState(0);

  const [item, setItem] = React.useState<any>(null);
  const [extras, setExtras] = React.useState<ExtraRow[]>([]);
  const [allFeatures, setAllFeatures] = React.useState<FeatureRow[]>([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = React.useState<number[]>(
    [],
  );

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);

  // slug auto until touched
  const [slugTouched, setSlugTouched] = React.useState(false);

  // coords paste
  const [coordsText, setCoordsText] = React.useState("");

  const load = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await adminFetch(`/api/admin/properties/${id}`);
      const data = await res.json().catch(() => ({}));
      const found = data?.item ?? null;
      setItem(found);

      const [rExtras, rAllFeat, rSelFeat] = await Promise.all([
        adminFetch(`/api/admin/properties/${id}/extras`).catch(() => null),
        adminFetch(`/api/admin/features`).catch(() => null),
        adminFetch(`/api/admin/properties/${id}/features`).catch(() => null),
      ]);

      if (rExtras?.ok) {
        const d = await rExtras.json().catch(() => ({}));
        setExtras(Array.isArray(d.items) ? d.items : []);
      } else setExtras([]);

      if (rAllFeat?.ok) {
        const d = await rAllFeat.json().catch(() => ({}));
        setAllFeatures(Array.isArray(d.items) ? d.items : []);
      } else setAllFeatures([]);

      if (rSelFeat?.ok) {
        const d = await rSelFeat.json().catch(() => ({}));
        setSelectedFeatureIds(Array.isArray(d.feature_ids) ? d.feature_ids : []);
      } else setSelectedFeatureIds([]);
    } catch {
      setErr("Αποτυχία φόρτωσης.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    load();
  }, [load]);

  const set = (k: string, v: any) => setItem((s: any) => ({ ...s, [k]: v }));

  React.useEffect(() => {
    if (!item) return;
    if (!slugTouched) {
      setItem((s: any) => ({ ...s, slug: slugify(s.title) }));
    }
  }, [item?.title, slugTouched]);

  const applyCoords = () => {
    const raw = (coordsText || "").trim();
    if (!raw || !item) return;

    const parts = raw.split(/[\s,]+/).filter(Boolean);
    if (parts.length < 2) {
      setErr("Δώσε συντεταγμένες ως: 40.25..., 23.51...");
      return;
    }
    const lat = Number(parts[0]);
    const lng = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setErr("Μη έγκυρες συντεταγμένες.");
      return;
    }
    setErr(null);
    set("latitude", lat);
    set("longitude", lng);
  };

  const saveProperty = async () => {
    if (!item || !id) return;
    setErr(null);
    setOk(null);
    setSaving(true);

    try {
      const res = await adminFetch(`/api/admin/properties/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: item.title,
          slug: item.slug,
          listing_status: item.listing_status,
          status: item.status,
          property_type: item.property_type,
          label: item.label,

          city: item.city,
          region: item.region,
          neighborhood: item.neighborhood,
          address: item.address,
          zip: item.zip,

          latitude: item.latitude != null ? Number(item.latitude) : null,
          longitude: item.longitude != null ? Number(item.longitude) : null,

          bedrooms: item.bedrooms != null ? Number(item.bedrooms) : null,
          bathrooms: item.bathrooms != null ? Number(item.bathrooms) : null,
          size_m2: item.size_m2 != null ? Number(item.size_m2) : null,

          persons_min:
            item.persons_min != null ? Number(item.persons_min) : null,
          persons_max:
            item.persons_max != null ? Number(item.persons_max) : null,

          price_per_night:
            item.price_per_night != null ? Number(item.price_per_night) : null,
          min_price_per_night:
            item.min_price_per_night != null
              ? Number(item.min_price_per_night)
              : null,
          min_stay_nights:
            item.min_stay_nights != null ? Number(item.min_stay_nights) : 1,

          featured_image_url: item.featured_image_url,
          excerpt: item.excerpt,
          content: item.content,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(data?.error || "Αποτυχία αποθήκευσης.");
        return;
      }

      await adminFetch(`/api/admin/properties/${id}/extras`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: extras }),
      }).catch(() => null);

      await adminFetch(`/api/admin/properties/${id}/features`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feature_ids: selectedFeatureIds }),
      }).catch(() => null);

      setOk("Αποθηκεύτηκε.");
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Typography>Φόρτωση...</Typography>;
  if (!item) return <Typography>Δεν βρέθηκε ακίνητο.</Typography>;

  const c = calcCompleteness(item);

  // ---- Extras helpers
  const addExtra = () => setExtras((s) => [...s, { title: "", value: "" }]);

  const removeExtra = (idx: number) =>
    setExtras((s) => s.filter((_, i) => i !== idx));

  const updateExtra = (idx: number, k: keyof ExtraRow, v: any) =>
    setExtras((s) => s.map((x, i) => (i === idx ? { ...x, [k]: v } : x)));

  // ---- Features helpers
  const toggleFeature = (fid: number) =>
    setSelectedFeatureIds((s) =>
      s.includes(fid) ? s.filter((x) => x !== fid) : [...s, fid],
    );

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Stack spacing={0.3}>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Edit · {item.title || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            #{item.id} · {item.slug}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`Completeness ${c.pct}%`}
            color={c.pct >= 80 ? "success" : c.pct >= 60 ? "warning" : "error"}
            variant="outlined"
          />
          <Button
            variant="outlined"
            onClick={() => nav(`/${currentLocale}/admin/properties`)}
          >
            Λίστα
          </Button>
          <Button
            variant="contained"
            onClick={saveProperty}
            disabled={saving}
            sx={{ fontWeight: 900 }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </Stack>

      {err ? <Alert severity="error">{err}</Alert> : null}
      {ok ? <Alert severity="success">{ok}</Alert> : null}

      <Paper variant="outlined" sx={{ borderRadius: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Basics" />
          <Tab label="Gallery" />
          <Tab label="Extras" />
          <Tab label="Features" />
        </Tabs>
        <Divider />

        <Box sx={{ p: 2 }}>
          {/* TAB 0: BASICS */}
          {tab === 0 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 900 }}>Βασικά</Typography>

              <FormGrid>
                <Col xs={12} md={6}>
                  <TextField
                    label="Title"
                    fullWidth
                    value={item.title || ""}
                    onChange={(e) => set("title", e.target.value)}
                  />
                </Col>

                <Col xs={12} md={6}>
                  <TextField
                    label="Slug"
                    fullWidth
                    value={item.slug || ""}
                    onChange={(e) => {
                      setSlugTouched(true);
                      set("slug", e.target.value);
                    }}
                  />
                </Col>

                <Col xs={12}>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>
                    Χαρακτηριστική εικόνα (Featured)
                  </Typography>

                  {item.featured_image_url ? (
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 520,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <img
                        src={item.featured_image_url}
                        alt=""
                        style={{ width: "100%", height: 240, objectFit: "cover", display: "block" }}
                      />
                    </Box>
                  ) : (
                    <Typography color="text.secondary">Δεν έχει οριστεί featured εικόνα.</Typography>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => setTab(1)} // πάει στο Gallery tab
                      sx={{ fontWeight: 900 }}
                    >
                      Άλλαξε από το Gallery
                    </Button>

                    {item.featured_image_url ? (
                      <Chip
                        label="Ορίζεται από τη Gallery επιλογή"
                        variant="outlined"
                        sx={{ alignSelf: "center" }}
                      />
                    ) : null}
                  </Stack>
                </Col>



                <Col xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={item.status || "draft"}
                      onChange={(e) => set("status", String(e.target.value))}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>

                <Col xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Listing</InputLabel>
                    <Select
                      label="Listing"
                      value={item.listing_status || "rent"}
                      onChange={(e) =>
                        set("listing_status", String(e.target.value))
                      }
                    >
                      {LISTING_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>

                <Col xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Property type</InputLabel>
                    <Select
                      label="Property type"
                      value={item.property_type || "apartment"}
                      onChange={(e) =>
                        set("property_type", String(e.target.value))
                      }
                    >
                      {PROPERTY_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>

                <Col xs={12} md={6}>
                  <TextField
                    label="Coordinates (lat, lng)"
                    placeholder="40.25536429456919, 23.517464052466593"
                    fullWidth
                    value={coordsText}
                    onChange={(e) => setCoordsText(e.target.value)}
                    onBlur={applyCoords}
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    label="Latitude"
                    type="number"
                    fullWidth
                    value={item.latitude ?? ""}
                    onChange={(e) =>
                      set(
                        "latitude",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    label="Longitude"
                    type="number"
                    fullWidth
                    value={item.longitude ?? ""}
                    onChange={(e) =>
                      set(
                        "longitude",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </Col>

                {/* υπόλοιπα basics όπως πριν */}
                <Col xs={12} md={4}>
                  <TextField
                    label="City"
                    fullWidth
                    value={item.city || ""}
                    onChange={(e) => set("city", e.target.value)}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <TextField
                    label="Region"
                    fullWidth
                    value={item.region || ""}
                    onChange={(e) => set("region", e.target.value)}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <TextField
                    label="Label"
                    fullWidth
                    value={item.label || ""}
                    onChange={(e) => set("label", e.target.value)}
                  />
                </Col>

                <Col xs={12}>
                  <TextField
                    label="Address"
                    fullWidth
                    value={item.address || ""}
                    onChange={(e) => set("address", e.target.value)}
                  />
                </Col>

                <Col xs={12} md={2}>
                  <TextField
                    label="Beds"
                    type="number"
                    fullWidth
                    value={item.bedrooms ?? ""}
                    onChange={(e) =>
                      set(
                        "bedrooms",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </Col>

                <Col xs={12} md={2}>
                  <TextField
                    label="Baths"
                    type="number"
                    fullWidth
                    value={item.bathrooms ?? ""}
                    onChange={(e) =>
                      set(
                        "bathrooms",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </Col>

                <Col xs={12} md={2}>
                  <TextField
                    label="Size m²"
                    type="number"
                    fullWidth
                    value={item.size_m2 ?? ""}
                    onChange={(e) =>
                      set(
                        "size_m2",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    label="Persons min"
                    type="number"
                    fullWidth
                    value={item.persons_min ?? ""}
                    onChange={(e) =>
                      set(
                        "persons_min",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    label="Persons max"
                    type="number"
                    fullWidth
                    value={item.persons_max ?? ""}
                    onChange={(e) =>
                      set(
                        "persons_max",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    label="Price / night"
                    type="number"
                    fullWidth
                    value={item.price_per_night ?? ""}
                    onChange={(e) =>
                      set(
                        "price_per_night",
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    label="Min stay nights"
                    type="number"
                    fullWidth
                    value={item.min_stay_nights ?? 1}
                    onChange={(e) =>
                      set(
                        "min_stay_nights",
                        e.target.value ? Number(e.target.value) : 1,
                      )
                    }
                  />
                </Col>

                <Col xs={12}>
                  <TextField
                    label="Excerpt"
                    fullWidth
                    multiline
                    minRows={2}
                    value={item.excerpt || ""}
                    onChange={(e) => set("excerpt", e.target.value)}
                  />
                </Col>

                <Col xs={12}>
                  <TextField
                    label="Content (HTML)"
                    fullWidth
                    multiline
                    minRows={6}
                    value={item.content || ""}
                    onChange={(e) => set("content", e.target.value)}
                  />
                </Col>
              </FormGrid>
            </Stack>
          ) : null}

          {/* TAB 1: GALLERY (✅ εδώ μόνο) */}
          {tab === 1 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 900 }}>Photos</Typography>
              <AdminImagesPanel
                propertyId={Number(item.id)}
                featuredImageUrl={item.featured_image_url || null}
                onFeaturedChange={(url) => set("featured_image_url", url)}
              />
            </Stack>
          ) : null}

          {/* TAB 2: EXTRAS */}
          {tab === 2 ? (
            <Stack spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={{ fontWeight: 900 }}>Extras</Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={addExtra}
                >
                  Add extra
                </Button>
              </Stack>

              {extras.length === 0 ? (
                <Typography color="text.secondary">
                  Δεν υπάρχουν extras ακόμα.
                </Typography>
              ) : null}

              <Stack spacing={2}>
                {extras.map((ex, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography sx={{ fontWeight: 800 }}>
                        Extra #{idx + 1}
                      </Typography>
                      <IconButton
                        onClick={() => removeExtra(idx)}
                        aria-label="delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>

                    <FormGrid>
                      <Col xs={12} md={6}>
                        <TextField
                          label="title"
                          fullWidth
                          value={ex.title || ""}
                          onChange={(e) =>
                            updateExtra(idx, "title", e.target.value)
                          }
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <TextField
                          label="value"
                          fullWidth
                          value={ex.value || ""}
                          onChange={(e) =>
                            updateExtra(idx, "value", e.target.value)
                          }
                        />
                      </Col>
                    </FormGrid>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          ) : null}

          {/* TAB 3: FEATURES */}
          {tab === 3 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 900 }}>Features</Typography>

              {allFeatures.length === 0 ? (
                <Alert severity="info">
                  Δεν βρέθηκαν διαθέσιμα features. (Χρειάζεται endpoint
                  /api/admin/features)
                </Alert>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                    gap: 1,
                  }}
                >
                  {allFeatures.map((f) => (
                    <Paper
                      key={f.id}
                      variant="outlined"
                      sx={{ p: 1.2, borderRadius: 2 }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedFeatureIds.includes(f.id)}
                            onChange={() => toggleFeature(f.id)}
                          />
                        }
                        label={f.name}
                      />
                    </Paper>
                  ))}
                </Box>
              )}
            </Stack>
          ) : null}
        </Box>
      </Paper>
    </Stack>
  );
}
